import { XIcon } from "icons/outline";
import { SearchIcon } from "icons/solid";
import { useRouter } from "next/router";
import { useUpdateEffect } from "react-use";
import { Badge, cn } from "ui";

import { useEffect, useRef, useState } from "react";
import Portal from "./Portal";

export const GlobalSearch = ({ scope }: { scope: string }) => {
  const router = useRouter();
  const [search, setSearch] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const badgeRef = useRef<HTMLInputElement>(null);

  // Sync input state, if search query changed from outside
  useEffect(() => {
    if (!router.isReady) return;
    setSearch((router.query.search as string) || "");
  }, [router]);

  /**
   * Debounce updating URL to avoid multiple rerenders
   * reset page query on global search changes
   * remove search query param from url if has empty string value
   */
  useUpdateEffect(() => {
    const timerId = setTimeout(() => {
      const { search: _, page, ...restQuery } = router.query;

      if (search) {
        router.replace({
          query: { ...restQuery, search, ...(page ? { page: 1 } : {}) },
        });
      } else {
        router.replace({
          query: { ...restQuery, ...(page ? { page: 1 } : {}) },
        });
      }
    }, 450);

    return () => clearTimeout(timerId);
  }, [search]);

  return (
    <Portal containerId="global-search-portal">
      <div className="group relative flex items-center md:min-w-[600px]">
        <div
          onClick={() => {
            inputRef.current?.focus();
          }}
          ref={badgeRef}
          className="flex shrink-0 items-center text-foundations-subtler group-hover:text-foundations-subtle"
        >
          <SearchIcon className="w-6" />
          <Badge className="ml-2" variant="default">
            <span className="capitalize text-foundations-subtle">{scope}</span>
          </Badge>
        </div>
        {/* NOTE: don't move elements order here to apply peer styles */}
        <input
          ref={inputRef}
          className={cn(
            "peer relative z-10 w-full border-none bg-transparent px-2 py-4 text-foundations-bolder placeholder:text-foundations-subtler focus:outline-none focus:ring-0"
          )}
          placeholder="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => {
            badgeRef.current?.classList.add("text-foundations-bolder");
            badgeRef.current?.classList.remove(
              "text-foundations-subtler",
              "group-hover:text-foundations-subtle"
            );
          }}
          onBlur={() => {
            badgeRef.current?.classList.remove("text-foundations-bolder");
            badgeRef.current?.classList.add(
              "text-foundations-subtler",
              "group-hover:text-foundations-subtle"
            );
          }}
        />
        <div className="flex items-center peer-placeholder-shown:hidden">
          <button
            className="absolute right-2 z-10 p-3 text-foundations-subtler hover:text-foundations-subtle"
            onClick={() => setSearch("")}
          >
            <XIcon className="w-4" />
          </button>
        </div>
      </div>
    </Portal>
  );
};

export function useGlobalSearchQuery() {
  const router = useRouter();
  return router.query.search;
}
